/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2018, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'../../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'../../../../widgets/livewidget/nls/StringResources',
	'../api/SlotActionsProviderAPI',
	'../../../../apiHelpers/SlotAPIHelper'
], (APIFactory, stringResources, SlotActionsProviderAPI, SlotAPIHelper) => {

	class BinAction {

		constructor({ content, dashboardAPI, features }) {
			this.content = content;
			this.dashboard = dashboardAPI;
			this.icons = features && features['Dashboard.Icons'];
			this.content.getFeature('SlotActions').registerProvider('autoBinAction', this.getAPI());
		}

		getAPI() {
			if (!this.api) {
				this.api = APIFactory.createAPI(this, [ SlotActionsProviderAPI ]);
			}
			return this.api;
		}

		_isOlap(dataSource, dataItem) {
			return !!(dataSource.isOlapPackage() || dataItem && dataItem.getMetadataColumn().isOlapColumn());
		}

		_supportAction(slot, itemIndex) {
			// Olap sources are unsupported, for now.
			const visualization = this.content.getFeature('Visualization');
			const dataSource = visualization.getDataSource();
			const dataItem =  slot.getDataItemList()[itemIndex];
			const isOlap = this._isOlap(dataSource, dataItem);
			let unsupportedSource = !dataSource || isOlap;
			const definition = visualization.getDefinition();
			return itemIndex !== undefined &&
				(definition && definition.getProperty('canApplyBinning')) &&
				!unsupportedSource &&
				!(SlotAPIHelper.isMultiMeasuresSeriesSlot(slot) || SlotAPIHelper.isMultiMeasuresValueSlot(slot) )&&
				this.isSlotTypeOrDataItemTypeSupportBinning(slot, dataItem, itemIndex);
		}

		/**
		 *
		 * @param {Object} slot - slot api for slot we're checking against
		 * @param {Integer} index - index of the data item we want to check, can be derived from slotState.mapIndex
		 * @return {Boolean} returns true if the slot has autobinning enabled and set to context
		 */
		isSlotTypeOrDataItemTypeSupportBinning(slot, dataItem, index) {
			if (slot.getDefinition().getType() !== 'ordinal') {
				// normal case, or case where we already have binned data items
				if (dataItem.getMetadataColumn().getType() === 'fact') {
					return true;
				}
				if (slot.getDataItemList()[index].getType() === 'fact') {
					return true;
				}
			}
			return false;
		}

		_getViewState(slot, itemIndex) {
			const definition = this.content.getFeature('Visualization').getDefinition();

			if (slot) {
				// @todo get slot from visualizationAPI and use new SLOTAPI
				const dataItem = slot.getDataItemList()[itemIndex];

				if (dataItem) {
					const viewState = {
						height: 280,
						width: 400, // those are hints only; for the placement of the toolbar.,
						defaultNumberOfBins: definition.getProperty('binningConfig').defaultNumberOfBins,
						binningStateCb: dataItem.getBinning.bind(dataItem)
					};
					return viewState;
				}
			}
			return null;
		}

		/**
		 * Find the correct data item index
		 * @param {Object} slot - slot api for slot we're checking against
		 * @return {Integer || undefined} Returns the index of the first data item in this slot that has the appropriate type for autobinning or undefined if none is found
		 */
		_findDataItemIndex(slot) {
			let index;
			// when this path is triggered from rclick on axis title, we get the wrong mapindex for the data item
			// index is null but we have data items, maybe 1st dataitem isnt the right one which is the behaviour we saw by letting the incorrect index propagate forward
			// works fine if its from data slots view, mapindex comes in defined and correct
			if (slot.getDataItemList().length > 0) {
				const dataItems = slot.getDataItemList();
				dataItems.find((dataItem, idx) => {
					const metaDataColumn = dataItem.getMetadataColumn();
					if (metaDataColumn && metaDataColumn.getType() === 'fact') {
						index = idx;
						return true;
					}
				});
			}
			return index;
		}

		getSlotActionList(slotId, itemIndex) {
			const visualization = this.content.getFeature('Visualization');
			const slot = visualization.getSlots().getSlot(slotId);
			if (itemIndex === null || itemIndex === undefined) {
				itemIndex = this._findDataItemIndex(slot);
			}
			if (slot && this._supportAction(slot, itemIndex)) {
				let viewOptions = this._getViewState(slot, itemIndex);
				const autoBinIcon = this.icons.getIcon('dashboard-autobin');
				return [{
					name: 'autoBinAction',
					label: stringResources.get('autoBinActionText'),
					icon: autoBinIcon.id,
					type: 'NextView',
					view: {
						module: 'dashboard-analytics/dialogs/AutobinDialog',
						state: viewOptions
					},
					actions: {
						apply: this._applyBinning.bind(this, slot, itemIndex)
					}
				}];
			}
			return [];
		}

		_applyBinning(slot, itemIndex, binningState) {
			const dataItem = slot.getDataItemList()[itemIndex];
			if (binningState) {
				dataItem.setBinning({
					bins: binningState.numberOfBins
				});
			} else {
				dataItem.setBinning(null);
			}
		}
	}

	return BinAction;

});
